import React from 'react';
import { InputWrapper } from '../../../common/input-wrapper';
import { RbacObject } from '../../../../export-types/cleaned-types';
import { Rbac } from '../../../common/Rbac';
import { useProfile } from '../../../../api/profile';
import { FormattedMessage, useIntl } from 'react-intl';
import { trpcSwr } from '../../../../api/trpc';
import { CustomDeliveryRequestEditForm } from './customs-delivery-request-edit-form';
import { CustomsDeliveryRequestInfoActions } from './customs-delivery-request-info-actions';
import { Gallery } from '../../../common/gallery/gallery';
import { getFileV2ApiUrl, toMoney } from '../../../../utils/common.utils';
import { NavLink } from 'react-router-dom';
import { getPrefixByType } from '../../../common/cards/id-cell-card.component';

type Props = {
  id: string;
};

export const CustomsDeliveryOrderRequest: React.FC<Props> = (props) => {
  const intl = useIntl();
  const { data: user } = useProfile();
  const { data: order, mutate: orderMutate } =
    trpcSwr.customsDelivery.getOne.useSWR({ id: props.id });

  const [editMode, setEditMode] = React.useState(false);

  if (!order || !user) {
    return null;
  }

  function handleChange() {
    orderMutate();
  }

  function handleSave() {
    orderMutate();
    setEditMode(false);
  }

  function handleEdit() {
    setEditMode(true);
  }

  function handleCancel() {
    setEditMode(false);
  }

  if (editMode) {
    return (
      <CustomDeliveryRequestEditForm
        id={order.id}
        defaultValues={{
          name: order.name || '',
          weight: order.weight,
          volume: order.volume,
          city: order.city,
          files: order.files.map((file) => ({
            type: 'exist',
            file: file,
          })),
          price: order.price,
          amount: order.amount,
        }}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  const requestInfoActions = (
    <CustomsDeliveryRequestInfoActions
      order={order}
      onChange={handleChange}
      onEdit={handleEdit}
    />
  );

  return (
    <div>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">
          <FormattedMessage
            defaultMessage="О заказе"
            id="orderInfo.label.aboutOrder"
            description="Информация о заказе"
          />
        </div>
        {[
          'created',
          'preparing',
          'codeSelected',
          'calculationReady',
          'protectCalculation',
          'prepareDocs',
          'prepareInvoice',
          'declarant',
          'waitingForPayment',
          'buyOut',
          'inWork',
          'inStock',
        ].includes(order.status) && (
          <Rbac
            object={RbacObject.CustomsDelivery}
            action={'write'}
            children={requestInfoActions}
          />
        )}
      </div>
      <div className={'d-flex gap-2 w-100'}>
        {order.name && (
          <div className="mt-3 w-50">
            <div className="mb-1 color-gray-600">
              <FormattedMessage
                defaultMessage="Название"
                id="purchaseOrderRequest.label.name"
                description="Информация о товаре"
              />
            </div>
            <div className="text-black text-wrap text-break">{order.name}</div>
          </div>
        )}
        {order.weight && (
          <InputWrapper
            theme="light"
            className="mt-3"
            title={intl.formatMessage({
              defaultMessage: 'Вес',
              id: 'customsDeliveryOrder.input-label.weight',
              description: 'Заголовок поля ввода',
            })}
          >
            {order.weight} кг
          </InputWrapper>
        )}
      </div>

      <div className={'d-flex gap-2 w-100'}>
        {order.price && (
          <div className="mt-3 w-50">
            <div className="mb-1 color-gray-600">
              <FormattedMessage
                defaultMessage="Общая стоимость"
                id="purchaseOrderRequest.label.price"
                description="Информация о товаре"
              />
            </div>
            <div className="text-black text-wrap text-break">
              {toMoney(order.price, 'cny')}
            </div>
          </div>
        )}
        {order.volume && (
          <InputWrapper
            theme="light"
            className="mt-3"
            title={intl.formatMessage({
              defaultMessage: 'Объем',
              id: 'customsDeliveryOrder.input-label.volume',
              description: 'Заголовок поля ввода',
            })}
          >
            {order.volume} м³
          </InputWrapper>
        )}
      </div>
      <div className={'d-flex gap-2 w-100'}>
        {' '}
        {order.amount && (
          <div className="mt-3 w-50">
            <div className="mb-1 color-gray-600">
              <FormattedMessage
                defaultMessage="Общее кол-во"
                id="purchaseOrderRequest.label.amount"
                description="Информация о товаре"
              />
            </div>
            <div className="text-black text-wrap text-break">
              {order.amount}
            </div>
          </div>
        )}
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage({
            defaultMessage: 'Расчетный объем',
            id: 'customsDeliveryOrder.input-label.estimatedVolume',
            description: 'Заголовок поля ввода',
          })}
        >
          {order.estimatedVolume} м³
        </InputWrapper>
      </div>

      {order.files?.length ? (
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage({
            defaultMessage: 'Файлы',
            id: 'purchaseOrderRequest.input-label.files',
            description: 'Заголовок поля ввода',
          })}
        >
          <Gallery className="mt-2">
            {order.files.map((file, index) => (
              <a href={getFileV2ApiUrl(file.id)} key={index}>
                <img
                  className="rounded fit-cover me-2"
                  src={getFileV2ApiUrl(file.id, '64x64')}
                  width={64}
                  height={64}
                />
              </a>
            ))}
          </Gallery>
        </InputWrapper>
      ) : null}

      {order.purchaseOrder ? (
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage({
            defaultMessage: 'Связанный заказ',
            id: 'CustomsDeliveryOrderRequest.label-input.order',
            description: 'Информация о связанных посылках',
          })}
        >
          <NavLink
            to={`/${order.purchaseOrder.type === 'parcel' ? 'parcels' : 'purchases'}/${order.purchaseOrder.id}`}
          >
            {getPrefixByType(order.purchaseOrder.type)}
            {order.purchaseOrder.internalId}
          </NavLink>
        </InputWrapper>
      ) : null}
    </div>
  );
};
